import { getSkiPassPriceAtTripStart } from '@/utils/calculations';
import { Trip, Apartment, BillData, ApartmentType, SkiPass, Traveller, TransportationOption } from '@/types';

// These calculation functions are pure and can be kept separate or moved inside the service
export const getSkiPassPrice = (traveller: Traveller, skiPasses: SkiPass[], trip: Trip): number => {
    if (!traveller.skiPassType) return 0;
    const skiPass = skiPasses.find(pass => pass.id === traveller.skiPassType);
    if (!skiPass) return 0;

    // Use centralized function that calculates age at trip start date with configured age levels
    return getSkiPassPriceAtTripStart(
        traveller.dateOfBirth, 
        trip.startDate, 
        trip.skiPassAgeLevels, 
        skiPass
    );
};

export const getTransportationPrice = (traveller: Traveller, transportationOptions: TransportationOption[]): number => {
    if (!traveller.transportationType) return 0;
    const transportation = transportationOptions.find(t => t.id === traveller.transportationType);
    return transportation ? transportation.pricePerPerson : 0;
};

export const getMembershipFee = (traveller: Traveller, trip: Trip): number => {
    if (traveller.isSkiClubMember) return 0;
    return traveller.membershipFee || trip.defaultMembershipFee || 35;
};

export const getApartmentPrice = (apartment: Apartment, apartmentTypes: ApartmentType[]): number => {
    const apartmentType = apartmentTypes.find(s => s.id === apartment.apartmentTypeId);
    if (!apartmentType) return 0;

    const occupancy = apartment.currentOccupancy;
    const priceRule = apartmentType.priceRules.find(rule => rule.occupancy === occupancy) ||
        apartmentType.priceRules.find(rule => rule.occupancy <= occupancy) ||
        apartmentType.priceRules[0];

    return priceRule ? priceRule.pricePerPersonPerWeek : 0;
};

export const getBillDataForTrip = (trip: Trip, apartments: Apartment[], travellers: Traveller[]): BillData[] => {
    return apartments.map(apartment => {
        const apartmentTravellers = travellers.filter(t => t.apartmentId === apartment.id);
        const accommodationCostPerPerson = getApartmentPrice(apartment, trip.apartmentTypes);

        const travellersData = apartmentTravellers.map(traveller => {
            const accommodationCost = accommodationCostPerPerson;
            const skiPassCost = getSkiPassPrice(traveller, trip.skiPasses, trip);
            const transportationCost = getTransportationPrice(traveller, trip.transportationOptions);
            const membershipFee = getMembershipFee(traveller, trip);

            return {
                name: traveller.name,
                accommodationCost,
                skiPassCost,
                transportationCost,
                membershipFee,
                total: accommodationCost + skiPassCost + transportationCost + membershipFee
            };
        });

        const totalAccommodation = travellersData.reduce((sum, t) => sum + t.accommodationCost, 0);
        const totalSkiPass = travellersData.reduce((sum, t) => sum + t.skiPassCost, 0);
        const totalTransportation = travellersData.reduce((sum, t) => sum + t.transportationCost, 0);
        const totalMembership = travellersData.reduce((sum, t) => sum + t.membershipFee, 0);

        return {
            apartmentId: apartment.id,
            apartmentName: apartment.name,
            travellers: travellersData,
            totalAccommodation,
            totalSkiPass,
            totalTransportation,
            totalMembership,
            grandTotal: totalAccommodation + totalSkiPass + totalTransportation + totalMembership,
            billStatus: apartment.billStatus || 'not_sent',
            isPaid: apartment.isPaid || false
        };
    });
};

export const exportBillsToCsv = (trip: Trip, apartments: Apartment[], travellers: Traveller[]) => {
    const billData = getBillDataForTrip(trip, apartments, travellers);

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Apartment,Traveller,Accommodation,Ski Pass,Transportation,Membership,Total,Bill Status,Paid\n";

    billData.forEach(bill => {
        bill.travellers.forEach(traveller => {
            csvContent += `"${bill.apartmentName}","${traveller.name}",${traveller.accommodationCost},${traveller.skiPassCost},${traveller.transportationCost},${traveller.membershipFee},${traveller.total},"${bill.billStatus}",${bill.isPaid}\n`;
        });
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${trip.name}_bills.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
